// StyleGenome brand system — strict 3-tier hierarchy.
//
//   1. LogoMark      — SG monogram only. PRIMARY visual anchor.
//                      Used on: nav (mobile + desktop), favicon, splash, PWA icon, login.
//   2. Wordmark      — "StyleGenome" text only. Pairs with LogoMark below it.
//   3. FullLockup    — Monogram + wordmark + tagline (the bundled marketing PNGs).
//                      Used ONLY in: landing hero + footer + legal pages.
//
// Rule: never use FullLockup in nav, header, login, splash, or favicon.
// The bundled lockup PNG would be unreadable / cramped at those sizes.

const ASSETS = {
  mark:           '/brand/app_icon_1024.png',          // SG monogram (square)
  lockup_primary: '/brand/primary_lockup.png',         // full lockup, full colour, on ivory
  lockup_dark:    '/brand/ivory_on_dark.png',          // full lockup for dark backgrounds
  lockup_light:   '/brand/black_on_ivory.png',         // full lockup for light backgrounds
};

// =========================================================================
// 1) Mark — the SG monogram. Square. Use this 90% of the time.
// =========================================================================
export const LogoMark = ({ size = 32, className = '', testId = 'brand-mark' }) => (
  <img src={ASSETS.mark} alt="StyleGenome" data-testid={testId}
       width={size} height={size} draggable={false}
       style={{ width: `${size}px`, height: `${size}px`, display: 'block' }}
       className={`select-none rounded-md ${className}`} />
);

// =========================================================================
// 2) Wordmark — "StyleGenome" set in our serif. Pairs below the Mark.
// =========================================================================
export const Wordmark = ({ className = '', testId = 'brand-wordmark' }) => (
  <p data-testid={testId}
     className={`font-serif text-2xl tracking-tight text-[#0D0D0D] ${className}`}>
    StyleGenome
  </p>
);

// =========================================================================
// 3) Full Lockup — only for landing hero / footer / legal pages.
// =========================================================================
export const FullLockup = ({ variant = 'primary', height = 80, className = '', testId = 'brand-lockup' }) => {
  const src = variant === 'dark' ? ASSETS.lockup_dark
            : variant === 'light' ? ASSETS.lockup_light
            : ASSETS.lockup_primary;
  return (
    <img src={src} alt="StyleGenome" data-testid={testId} draggable={false}
         style={{ height: `${height}px`, width: 'auto', display: 'block' }}
         className={`select-none ${className}`} />
  );
};

// =========================================================================
// Convenience compositions
// =========================================================================

// Login / Welcome screen: Mark (centered) + Wordmark + tagline beneath.
// Strict spec from brand correction.
export const LoginBrandStack = ({ tagline = 'Your AI Personal Stylist' }) => (
  <div className="flex flex-col items-center" data-testid="login-brand-stack">
    <LogoMark size={88} className="mb-4" />
    <Wordmark className="text-3xl mb-2" />
    <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-[#C7A96B]">{tagline}</p>
  </div>
);

// Splash / loading on dark background.
export const SplashBrandStack = () => (
  <div className="flex flex-col items-center" data-testid="splash-brand-stack">
    <LogoMark size={72} className="mb-4" />
    <p className="font-serif text-2xl tracking-tight text-[#F7F5F1]">StyleGenome</p>
  </div>
);

// Backwards-compat alias so existing imports of <Logo /> still resolve.
// In-app default = the monogram (NOT the bundled lockup).
export const Logo = ({ variant = 'mark', height, ...rest }) => {
  if (variant === 'mark' || variant === 'icon') {
    return <LogoMark size={height || 32} {...rest} />;
  }
  if (variant === 'wordmark') return <Wordmark {...rest} />;
  if (variant === 'primary')      return <FullLockup variant="primary" height={height || 80} {...rest} />;
  if (variant === 'black_ivory')  return <FullLockup variant="light"   height={height || 80} {...rest} />;
  if (variant === 'ivory_dark')   return <FullLockup variant="dark"    height={height || 80} {...rest} />;
  return <LogoMark size={height || 32} {...rest} />;
};

export default Logo;
